

import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useRememberMe } from '../../shared/hooks/useRememberMe';
import { useUser } from '../providers/userContext';
import { useDispatch } from 'react-redux';
import { setLoginTime, setUserData } from '../store/slices/userSlice';
import axiosInstance from '../../services/api/axiosInstance';

const ROLE_DASHBOARD = {
  TEACHER: '/teacher/dashboard',
  PRINCIPAL: '/admin/dashboard',
  SCHOOL_ADMIN: '/admin/dashboard',
  ADMIN: '/admin/dashboard',
  STUDENT: '/student/dashboard',
  SUPER_ADMIN: '/super_admin/dashboard',
};

const PUBLIC_PATHS = ['/login', '/forgot-password', '/reset-password', '/support'];

export default function AuthInitializer({ children }) {
  const [checking, setChecking] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();
  const { getSavedToken, clearSession } = useRememberMe();
  const { login, logout, setInitialized } = useUser();
  const dispatch = useDispatch();

  useEffect(() => {
    const init = async () => {
      const { token, role } = getSavedToken();

      // ── No token at all → not logged in, let routes handle it ──
      if (!token) {
        setInitialized();
        setChecking(false);
        return;
      }

      const isPublicPage = PUBLIC_PATHS.includes(location.pathname)
        || location.pathname === '/';

      // ── Hydrate cached identity while the token is validated below ──
      const rawUser = localStorage.getItem('user');
      let cachedUser = null;
      if (rawUser) {
        try {
          const userData = JSON.parse(rawUser);
          cachedUser = userData;

          // Hydrate Redux
          dispatch(setLoginTime(Date.now()));
          dispatch(setUserData({
            schoolId: userData.schoolId,
            schoolName: userData.schoolName,
            schoolCode: userData.schoolCode,
            schoolLogo: userData.schoolLogo,
            role: userData.roles?.[0],
            email: userData.email,
            username: userData.username,
            name: userData.name,
            permissions: userData.permission,
            subscriptionStatus: userData.subscriptionStatus,
            trialEndsIn: userData.trialEndsIn,
            googleDriveConnected: userData.googleDriveConnected,
          }));
          login({ ...userData, token });

        } catch {
          localStorage.removeItem('user');
          cachedUser = null;
        }
      }

      // Cached subscription and permission data must be revalidated on startup.
      try {
        const res = await axiosInstance.get('/auth/validate-token');
        if (!res.data.success) throw new Error('Validation failed');

        const userData = res.data.user;
        login({ ...userData, token: res?.data?.token || token });

        dispatch(setLoginTime(Date.now()));
        dispatch(setUserData({
          schoolId: userData.schoolId,
          schoolName: userData.schoolName,
          schoolCode: userData.schoolCode,
          schoolLogo: userData.schoolLogo,
          role: userData.roles?.[0],
          email: userData.email,
          username: userData.username,
          name: userData.name,
          permissions: userData.permission,
          subscriptionStatus: userData.subscriptionStatus,
          trialEndsIn: userData.trialEndsIn,
          googleDriveConnected: userData.googleDriveConnected,
          isOnboardComplete: userData.isOnboardComplete,
          hasPendingSetup: userData.hasPendingSetup,
          setupSkipped: userData.setupSkipped,
          pendingSetupItems: userData.pendingSetupItems ?? [],
        }));

        // ✅ Only redirect if on a public page
        if (isPublicPage) {
          const userRole = userData.roles?.[0] || role;
          navigate(ROLE_DASHBOARD[userRole] || '/admin/dashboard', { replace: true });
        }

      } catch (err) {
        console.error('Auth init failed:', err.message);
        const status = err.response?.status;
        const isInvalidSession = status === 401 || status === 403;

        if (isInvalidSession || !cachedUser) {
          logout();
          clearSession();
          navigate('/login', { replace: true });
        } else {
          // Keep the cached session during temporary API/network failures.
          // The token can still be used by protected screens and will be
          // checked again on the next application startup.
          const userRole = cachedUser.roles?.[0] || role;
          if (isPublicPage) {
            navigate(ROLE_DASHBOARD[userRole] || '/admin/dashboard', { replace: true });
          }
        }
      } finally {
        setInitialized();
        setChecking(false);
      }
    };

    init();
  }, []);

  if (checking) return <FullScreenLoader />;
  return children;
}

function FullScreenLoader() {
  return (
    <div style={{
      height: '100dvh', display: 'flex',
      alignItems: 'center', justifyContent: 'center',
    }}>
      <p style={{ fontSize: 14, color: 'var(--color-text-secondary)' }}>
        Loading your session...
      </p>
    </div>
  );
}